import React, { useState, useRef, useEffect } from 'react';
import { Country } from '../utils/types';

interface SearchableSelectProps {
  label: string;
  options: Country[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select a country...',
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options
    .filter(opt => {
      const search = searchTerm.toLowerCase().trim();
      if (!search) return true;
      return (
        opt.name.toLowerCase().includes(search) ||
        opt.code.toLowerCase().includes(search) ||
        (opt.aliases && opt.aliases.some(a => a.toLowerCase().includes(search)))
      );
    })
    .sort((a, b) => {
      const search = searchTerm.toLowerCase().trim();
      const aExact = a.code.toLowerCase() === search;
      const bExact = b.code.toLowerCase() === search;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      const aStarts = a.name.toLowerCase().startsWith(search);
      const bStarts = b.name.toLowerCase().startsWith(search);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.name.localeCompare(b.name);
    });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
        {label}
      </label>

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3.5 py-2.5 bg-white border rounded-xl text-sm transition-all text-left
          ${error ? 'border-rose-300 ring-1 ring-rose-200' : isOpen ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}
        `}
      >
        <span className={value ? 'text-slate-900 font-medium' : 'text-slate-400'}>
          {value || placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {error && <p className="mt-1 text-xs text-rose-500 font-medium">{error}</p>}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder-slate-400"
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <button
                  key={option.code}
                  type="button"
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 text-sm text-left transition-colors
                    ${value === option.name ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                  onClick={() => {
                    onChange(option.name);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  <span>{option.name}</span>
                  <span className="text-[10px] font-bold text-slate-300 tracking-wider">{option.code}</span>
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-sm text-center text-slate-400">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
